import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { GlueConfigurationService } from './glue-configuration.service';
import { AppUtils } from '../utils/app.utils';
import {
  ICart,
  ICartPayload,
  ICartUpdateRequest,
  ICartUpdateRequestAttributes,
  IVatIdData,
  IVatIdResponse,
} from '../models/cart.models';
import { CartUtils } from '../utils/cart.utils';
import { EGlueResource } from '../configurations/common';
import { IGuestCartResponse } from '../models/guest.models';
import { IStore } from '../models/settings.model';

@Injectable({
  providedIn: 'root',
})
export class ShoppingCartService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly glueConfiguration: GlueConfigurationService = inject(GlueConfigurationService);

  private readonly cartsUrl: string = `${this.glueConfiguration.getEndpointUrl()}/${EGlueResource.CARTS}`;
  private readonly guestCartsUrl: string = `${this.glueConfiguration.getEndpointUrl()}/${EGlueResource.GUEST_CARTS}`;

  // NOTE: consider introducing context-specific (per-store? per-call?) includes
  // e.g. sap-* Glue resources don't make sense for non-US stores
  private readonly httpParamsForGetCartItems: { params: HttpParams } = {
    params: new HttpParams().set(
      'include',
      [
        EGlueResource.CART_ITEMS,
        EGlueResource.CONCRETE_PRODUCTS,
        EGlueResource.CONCRETE_PRODUCT_IMAGE_SETS,
        EGlueResource.VOUCHERS,
        EGlueResource.CART_RULES,
        EGlueResource.RFQ_PRODUCTS,
        EGlueResource.SAP_MESSAGES,
        EGlueResource.SAP_ITEM_AVAILABILITIES,
        EGlueResource.SHIPMENTS,
      ].join(','),
    ),
  };

  private static handleError(err: any): Observable<any> {
    let errorMessage: string;
    if (Array.isArray(err)) {
      return throwError(err);
    } else if (err.error instanceof ErrorEvent) {
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      return throwError(err.status);
    }
    return throwError(errorMessage);
  }

  postCart(attributes: ICartUpdateRequestAttributes): Observable<ICartPayload> {
    const currentLangConfig: IStore = AppUtils.getCurrentStore();
    const postCartData: ICartUpdateRequest = {
      data: {
        type: EGlueResource.CARTS,
        attributes: {
          name: Date.now(),
          priceMode: currentLangConfig.storeDefaultPriceMode,
          currency: currentLangConfig.storeDefaultCurrency,
          store: currentLangConfig.storeId,
          ...attributes,
        },
      },
    };

    return this.httpClient.post<ICartPayload>(this.cartsUrl, postCartData, {
      ...this.httpParamsForGetCartItems,
      observe: 'response',
    }).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  getCarts(): Observable<any> {
    return this.httpClient.get<any>(`${this.cartsUrl}?include=items,concrete-product-image-sets,concrete-products`).pipe(
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  getGuestCartById(guestUserId: string, cartId: string): Observable<IGuestCartResponse> {
    return this.httpClient.get<any>(
      `${this.guestCartsUrl}/${cartId}?include=guest-cart-items,concrete-product-image-sets,concrete-products`,
      {
        headers: (new HttpHeaders({'X-Anonymous-Customer-Unique-Id': guestUserId})),
        observe: 'response',
      },
    ).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  getCartById(cartId: string): Observable<ICartPayload> {
    return this.httpClient.get<any>(`${this.cartsUrl}/${cartId}?include=vouchers`, {observe: 'response'}).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  deleteCartById(cartId: string): Observable<any> {
    const url: string = `${this.cartsUrl}/${cartId}`;
    return this.httpClient.delete<any>(url).pipe(
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  addItemToCart(cartId: string, itemData: any): Observable<ICartPayload> {
    const url: string = `${this.cartsUrl}/${cartId}/${EGlueResource.CART_ITEMS}`;
    return this.httpClient.post<ICartPayload>(
      url,
      itemData,
      {...this.httpParamsForGetCartItems, observe: 'response'},
    ).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors),
      ),
    );
  }

  postProductsFromCsvToCart(cartId: string, data: any): Observable<ICartPayload> {
    const url: string = `${this.cartsUrl}/${cartId}/${EGlueResource.ITEM_LISTS}`;
    return this.httpClient.post<any>(
      url,
      data,
      {...this.httpParamsForGetCartItems, observe: 'response'})
      .pipe(
        map((res: any) => this._mapResponseWithETag(res)),
        catchError((error) => {
            return ShoppingCartService.handleError(error.error.errors[0]);
          },
        ),
      );
  }

  getCartItems(cartId: string): Observable<ICartPayload> {
    const url: string = `${this.cartsUrl}/${cartId}`;
    return this.httpClient.get<any>(url, {...this.httpParamsForGetCartItems, observe: 'response'}).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  getCartShippingMethods(cartId: string): Observable<ICartPayload> {
    return this.httpClient.get<any>(`${this.cartsUrl}/${cartId}?include=shipments`, {observe: 'response'}).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  deleteItemFromCart(cartId: string, itemId: string): Observable<{}> {
    const url: string = `${this.cartsUrl}/${cartId}/${EGlueResource.CART_ITEMS}/${itemId}`;
    return this.httpClient.delete<any>(url).pipe(
      catchError((error) => ShoppingCartService.handleError(error.error)),
    );
  }


  deleteItemsFromCart(cartId: string): Observable<ICartPayload> {
    const url: string = `${this.cartsUrl}/${cartId}/${EGlueResource.ITEM_LISTS}/${cartId}`;
    return this.httpClient.delete<ICartPayload>(url, {...this.httpParamsForGetCartItems, observe: 'response'}).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) => {
        return ShoppingCartService.handleError(error.error.errors);
      }),
    );
  }

  sequentialRequestPatchCartItemGetCartItems(cartId: string, itemId: string, itemData: any): Observable<ICartPayload> {
    const urlPatch: string = `${this.cartsUrl}/${cartId}/${EGlueResource.CART_ITEMS}/${itemId}`;
    return this.httpClient.patch<any>(
      urlPatch,
      itemData,
      {...this.httpParamsForGetCartItems, observe: 'response'},
    ).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  /**
   * Validate VAT ID tax number
   * @param {string} vatId
   * @returns {Observable<IVatIdResponse>}
   */
  validateVatIdTaxNumber(vatId: string): Observable<IVatIdResponse> {
    const url: string = `${this.glueConfiguration.getEndpointUrl()}/tax-id-validate`;

    if (!vatId) {
      return throwError('VAT ID is required');
    }


    const body: IVatIdData = {
      data: {
        type: 'tax-id-validate',
        attributes: {
          taxId: vatId,
          // country code is always DE for EU VAT ID validation
          countryCode: 'DE',
        },
      },
    };

    return this.httpClient.post<IVatIdResponse>(url, body).pipe(
      take(1),
      map(res => {
        return res;
      }),
    );
  }

  postVoucher(cartId: string, code: any): Observable<ICartPayload> {
    const body: any = {
      data: {
        type: 'vouchers',
        attributes: {
          code,
        },
      },
    };
    return this.httpClient.post<any>(`${this.cartsUrl}/${cartId}/vouchers`, body, {
      ...this.httpParamsForGetCartItems,
      observe: 'response',
    }).pipe(
      map(res => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ));
  }

  removeVoucher(cartId: string, voucherId: string): Observable<ICartPayload> {
    const urlDelete: string = `${this.cartsUrl}/${cartId}/vouchers/${encodeURIComponent(voucherId)}`;
    return this.httpClient.delete<any>(urlDelete, {
      ...this.httpParamsForGetCartItems,
      observe: 'response',
    }).pipe(
      map(res => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  updateCart(cartId: string, cartData: ICartUpdateRequest, eTag: string): Observable<ICartPayload> {
    const urlUpdate: string = `${this.cartsUrl}/${cartId}`;
    return this.httpClient.patch<ICart>(
      urlUpdate,
      cartData,
      {
        ...this.httpParamsForGetCartItems,
        observe: 'response',
        headers: new HttpHeaders().set('If-Match', `${eTag}`),
      }).pipe(
      map((res: any) => this._mapResponseWithETag(res)),
      catchError((error) =>
        ShoppingCartService.handleError(error.error.errors[0]),
      ),
    );
  }

  /**
   * Get etag and add to the response
   * @param res
   * @returns {ICartPayload}
   * @private
   */
  private _mapResponseWithETag(res: any): ICartPayload {
    const eTag: string = res.headers.has('etag') ? CartUtils.getClearEtag(res.headers.get('etag')) : '';
    return {...res.body, eTag};
  }
}
